import React, { useState, useEffect, useCallback } from 'react';
import '../../styles/ExerciseLayout.css';
import QuestionLearnButton from '../full_mock_test/attempt/QuestionLearnButton';

const QuestionRow = ({ question, questionNumber, isExample, ASSET_BASE_URL, answer, handleSelect, showHint, isReviewMode, isCorrect, isHydrated }) => {
  const questionText = question.question_text.replace(/\(Example\)/gi, '').trim();
  const options = question.options || [];

  const resolveUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `${ASSET_BASE_URL}${url}`;
  };

  const isAnswerCorrect = isCorrect === true || isCorrect === 'true';
  const correctAnswer = question.accepted_answers?.[0];
  const selectedAnswer = isExample ? correctAnswer : (showHint || (isReviewMode && isAnswerCorrect)) ? correctAnswer : answer;
  const userAnswer = question.user_answer?.answer;

  return (
    <div className={`p-3 md:p-4 rounded-2xl border-2 transition-all ${isExample ? 'bg-blue-50/50 border-blue-100 dark:bg-blue-900/20 dark:border-blue-800' : 'bg-white border-slate-100 dark:bg-slate-800/50 dark:border-slate-700'}`}>
      <div className="flex items-center gap-2 mb-3">
        <span className="font-bold text-primary-blue text-lg flex-shrink-0">
          {isExample ? '' : `${questionNumber}.`}
        </span>
        <span className="font-display font-bold text-base md:text-lg text-slate-700 dark:text-slate-200">
          {questionText}
        </span>
        {isReviewMode && (
          <div className="ml-auto flex items-center gap-2 shrink-0">
            {isAnswerCorrect
              ? <span className="correct-checkmark">✓</span>
              : <span className="incorrect-checkmark">✗</span>
            }
            {!isExample && <QuestionLearnButton question={question} />}
          </div>
        )}
      </div>
      <div className="grid grid-cols-3 gap-2 md:gap-4">
        {options.map(option => {
          const isSelected = selectedAnswer === option.id;
          const isWrongAnswer = isReviewMode && !isAnswerCorrect && userAnswer === option.id;
          const isCorrectOption = isReviewMode && !isAnswerCorrect && correctAnswer === option.id;
          const disabled = isExample || isReviewMode || showHint || !isHydrated;

          return (
            <div
              key={option.id}
              className={`flex flex-col items-center gap-1 cursor-pointer transition-all ${disabled ? 'cursor-default' : ''}`}
              onClick={() => !disabled && handleSelect(question.id, option.id)}
            >
              <div className={`w-full rounded-xl border-2 overflow-hidden transition-all ${
                isWrongAnswer ? 'border-red-400 bg-red-50' :
                isCorrectOption ? 'border-green-400 bg-green-50' :
                isSelected ? 'border-primary-blue ring-2 ring-blue-200 shadow-md' :
                'border-slate-200 dark:border-slate-600'
              }`}>
                <img
                  src={resolveUrl(option.image_url)}
                  alt={`Option ${option.id}`}
                  className="w-full aspect-square object-contain bg-white p-1"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <span className="font-bold text-sm text-slate-600 dark:text-slate-400">{option.id}</span>
                <div className={`w-5 h-5 border-2 rounded flex items-center justify-center transition-all ${
                  isWrongAnswer ? 'border-red-400 bg-red-100' :
                  isCorrectOption ? 'border-green-400 bg-green-100' :
                  isSelected ? 'border-primary-blue bg-primary-blue' :
                  'border-slate-300 dark:border-slate-500'
                }`}>
                  {isSelected && <span className="text-white text-xs font-bold">✓</span>}
                  {isWrongAnswer && !isSelected && <span className="text-red-500 text-xs font-bold">✗</span>}
                  {isCorrectOption && <span className="text-green-600 text-xs font-bold">✓</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const StartersListenPart3 = ({ exerciseData, attemptId, exerciseId, showHint, isReviewMode, fillHintSignal }) => {
  const [answers, setAnswers] = useState({});
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    if (attemptId && exerciseId) {
      if (isReviewMode && exerciseData.questions) {
        const migratedAnswers = {};
        exerciseData.questions.forEach(q => {
          if (q.user_answer && typeof q.user_answer.answer === 'string') {
            migratedAnswers[q.id] = q.user_answer.answer;
          }
        });
        if (Object.keys(migratedAnswers).length > 0) {
          setAnswers(migratedAnswers);
          setIsHydrated(true);
          return;
        }
      }

      const attemptKey = `attempt_${attemptId}`;
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) {
        try {
          const allAnswers = JSON.parse(savedAttempt);
          const savedData = allAnswers[exerciseId];
          if (savedData && typeof savedData === 'object') {
            const migratedAnswers = {};
            Object.keys(savedData).forEach(qId => {
              const value = savedData[qId];
              migratedAnswers[qId] = (typeof value === 'object' && value !== null && 'answer' in value) ? value.answer : value;
            });
            setAnswers(migratedAnswers);
          }
        } catch (e) {
          console.error("Failed to parse saved attempt from localStorage", e);
        }
      }
      setIsHydrated(true);
    }
  }, [attemptId, exerciseId, isReviewMode, exerciseData.questions]);

  const saveAnswersToLocalStorage = useCallback((currentAnswers) => {
    if (!isHydrated || !attemptId || !exerciseId) return;

    const attemptKey = `attempt_${attemptId}`;
    let allAnswers = {};
    try {
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) allAnswers = JSON.parse(savedAttempt) || {};
    } catch (e) {
      allAnswers = {};
    }

    const answersToSave = {};
    Object.keys(currentAnswers).forEach((qId) => {
      answersToSave[qId] = { answer: currentAnswers[qId] };
    });

    allAnswers[exerciseId] = answersToSave;
    localStorage.setItem(attemptKey, JSON.stringify(allAnswers));
  }, [attemptId, exerciseId, isHydrated]);

  useEffect(() => {
    if (!isHydrated) return;

    const debounceSave = setTimeout(() => {
      saveAnswersToLocalStorage(answers);
    }, 500);

    return () => {
      clearTimeout(debounceSave);
      saveAnswersToLocalStorage(answers);
    };
  }, [answers, isHydrated, saveAnswersToLocalStorage]);

  useEffect(() => {
    if (fillHintSignal > 0) {
      const newAnswers = {};
      exerciseData.questions.forEach(q => {
        if (q.accepted_answers && q.accepted_answers.length > 0) {
          newAnswers[q.id] = q.accepted_answers[0];
        }
      });
      setAnswers(newAnswers);
      saveAnswersToLocalStorage(newAnswers);
    }
  }, [fillHintSignal, exerciseData.questions, saveAnswersToLocalStorage]);

  if (!exerciseData || !exerciseData.content_data || !exerciseData.questions) {
    return <div>Loading exercise data...</div>;
  }

  const sortedQuestions = [...exerciseData.questions].sort((a, b) => a.order_index - b.order_index);
  const exampleQuestions = sortedQuestions.filter(q => q.order_index === 0);
  const mainQuestions = sortedQuestions.filter(q => q.order_index > 0);

  const ASSET_BASE_URL = process.env.REACT_APP_MEDIA_PREFIX || 'http://localhost:8000';

  const handleSelect = (questionId, optionId) => {
    setAnswers(prev => ({ ...prev, [questionId]: optionId }));
  };

  return (
    <div className="w-full h-full p-1 md:px-10">
      <div className="relative flex flex-col items-center h-full bg-transparent rounded-2xl p-2 md:p-6 overflow-y-auto custom-scrollbar">
        <div className="max-w-3xl w-full bg-white dark:bg-slate-800/50 rounded-2xl border-2 border-gray-200 dark:border-slate-600 p-3 md:p-8 shadow-sm">
          <div className="flex flex-col gap-3">
            {exampleQuestions.map(q => (
              <QuestionRow
                key={q.id}
                question={q}
                isExample={true}
                ASSET_BASE_URL={ASSET_BASE_URL}
                answer={answers[q.id]}
                handleSelect={handleSelect}
                isReviewMode={isReviewMode}
                isCorrect={q.is_correct}
                isHydrated={isHydrated}
              />
            ))}
            {mainQuestions.map((q, index) => (
              <QuestionRow
                key={q.id}
                question={q}
                questionNumber={index + 1}
                isExample={false}
                ASSET_BASE_URL={ASSET_BASE_URL}
                answer={answers[q.id]}
                handleSelect={handleSelect}
                showHint={showHint}
                isReviewMode={isReviewMode}
                isCorrect={q.is_correct}
                isHydrated={isHydrated}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StartersListenPart3;
